// 견적 머리 칸(제목·관리비율·절사·조건·안내·평수) 변환 — 편집기와 서버 액션(잠김 응답의 lock_head)이 같이 쓴다.

import { m2ToPyeong, quoteAreaM2 } from "./area-unit";
import { defaultRounding, normalizeRate } from "./calc";
import type { CopyHead } from "./lock-reason";
import { Quote } from "./types";

/**
 * 저장된 절사액이 「자동」 이었는지 되짚는다.
 *
 * 저장소는 절사를 늘 숫자로 굳힌다(null 을 안 남긴다). 그대로 다시 열면
 * 자동으로 두었던 견적이 고정값이 되어, 항목을 고쳐도 총액이 만원 단위로 안 떨어지고
 * gross 가 줄면 절사액 > 총계로 계산이 막힌다. 저장값이 지금 gross 의 자동값과
 * 같으면 자동으로 간주해 되돌린다 — 사람이 그 값을 직접 넣었더라도 결과는 같다.
 */
const roundingOf = (
  saved: number | null,
  gross: number
): number | null =>
  saved === null || saved === defaultRounding(gross) ? null : saved;

// 평수는 양수만, DB `numeric(10,2)` 에 맞춰 둘째 자리까지 쓴다 — 0·음수·NaN 은 「없음」 이다.
export const areaOf = (value: unknown): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const rounded = Math.round(value * 100) / 100;
  return rounded > 0 ? rounded : null;
};

/** "4.5" → 0.045. 빈 칸·잘못된 값은 0, 100% 를 넘지 못한다. */
export const mgmtRateOf = (pct: string): number => {
  const n = Number(pct);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n / 100, 1);
};

// 견적 행 → 편집기 머리 칸. 첫 화면과 잠김 응답의 서버 머리(lock_head)가 같은 변환을 쓴다.
export const editorHeadOf = (
  quote: Pick<Quote, "title" | "mgmt_fee_rate" | "rounding_adjustment" | "gross" | "conditions" | "extra_notice" | "rough_area_pyeong"> & {
    area_m2?: number | null;
  }
): CopyHead => {
  // m² 원본 기준 — 평이 비어 있어도 m² 가 있으면 평을 m² 에서 채운다(작은 m² 가 사라지지 않게).
  const m2 = areaOf(quoteAreaM2(quote));
  const pyeong = areaOf(quote.rough_area_pyeong) ?? m2ToPyeong(m2);
  return {
    title: quote.title,
    mgmt_fee_pct: String(Number((quote.mgmt_fee_rate * 100).toFixed(4))),
    rounding: roundingOf(quote.rounding_adjustment, quote.gross),
    conditions: quote.conditions,
    extra_notice: quote.extra_notice,
    area_pyeong: pyeong,
    area_m2: pyeong === null ? null : m2,
  };
};

// 화면 머리 → 저장 뒤 다시 읽은 머리(저장 경로의 mgmtRateOf·normalizeRate·절사 기본값을 거쳐 editorHeadOf) — gross 를 못 셌으면 절사는 화면 값 그대로.
export const storedHeadOf = (head: CopyHead, gross: number | null): CopyHead => {
  const stored = editorHeadOf({
    title: head.title,
    mgmt_fee_rate: normalizeRate(mgmtRateOf(head.mgmt_fee_pct.trim())),
    rounding_adjustment: head.rounding ?? defaultRounding(gross ?? 0),
    gross: gross ?? 0,
    conditions: head.conditions,
    extra_notice: head.extra_notice,
    rough_area_pyeong: head.area_pyeong,
    area_m2: head.area_m2,
  });
  return gross === null ? { ...stored, rounding: head.rounding } : stored;
};
